import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useMembership } from '@/contexts/MembershipContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import {
  MessageSquare,
  Send,
  Trash2,
  RefreshCw,
  Lock,
  Users,
  ImagePlus,
  Video,
  X,
  Loader2,
} from 'lucide-react';


interface BoardPost {
  id: string;
  author_email: string;
  author_name: string;
  content: string;
  created_at: string;
  media_url?: string | null;
  media_type?: string | null;
}

const MAX_IMAGE_MB = 10;
const MAX_VIDEO_MB = 50;
const MAX_VIDEO_SECONDS = 60; // "short" video

const formatTime = (iso: string) => {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
};

const getVideoDuration = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => reject(new Error('Could not read video'));
    video.src = window.URL.createObjectURL(file);
  });

const MessageBoard: React.FC = () => {
  const { isMember, memberEmail, openMembershipModal } = useMembership();
  // Real auth user — gates the polling loop. Under DEV_BYPASS_AUTH there's
  // no real JWT, so any database read fails network-level and floods the
  // console. We do one initial fetch (which surfaces an empty list if
  // unauthorized, RLS-protected, or CORS-blocked) and then skip the
  // 15s poll entirely.
  const { user: realAuthUser } = useAuth();
  const hasRealSession = !!realAuthUser;
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [content, setContent] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [mediaKind, setMediaKind] = useState<'image' | 'video' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('board_posts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (!error && data) setPosts(data as BoardPost[]);
    } catch (err) {
      // Network / CORS failures are non-fatal — leave existing posts in place.
      console.warn('[MessageBoard] fetchPosts failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    // Only poll when there's a real auth session — otherwise we'd be firing
    // failed requests every 15s under DEV_BYPASS_AUTH.
    if (!hasRealSession) return;
    const interval = setInterval(fetchPosts, 15000);
    return () => clearInterval(interval);
  }, [fetchPosts, hasRealSession]);


  useEffect(() => {
    const saved = localStorage.getItem('kinscircle_board_name');
    if (saved) setAuthorName(saved);
  }, []);

  useEffect(() => {
    return () => {
      if (mediaPreview) URL.revokeObjectURL(mediaPreview);
    };
  }, [mediaPreview]);

  const clearMedia = () => {
    if (mediaPreview) URL.revokeObjectURL(mediaPreview);
    setMediaFile(null);
    setMediaPreview(null);
    setMediaKind(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      toast.error('Only images and videos are allowed');
      return;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (isImage && sizeMB > MAX_IMAGE_MB) {
      toast.error(`Image must be under ${MAX_IMAGE_MB} MB`);
      return;
    }
    if (isVideo && sizeMB > MAX_VIDEO_MB) {
      toast.error(`Video must be under ${MAX_VIDEO_MB} MB`);
      return;
    }

    if (isVideo) {
      try {
        const duration = await getVideoDuration(file);
        if (duration > MAX_VIDEO_SECONDS) {
          toast.error(`Video must be ${MAX_VIDEO_SECONDS} seconds or shorter`);
          return;
        }
      } catch {
        toast.error('Could not read that video file');
        return;
      }
    }

    if (mediaPreview) URL.revokeObjectURL(mediaPreview);
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
    setMediaKind(isImage ? 'image' : 'video');
  };

  const uploadMedia = async (): Promise<{ url: string; type: string } | null> => {
    if (!mediaFile) return null;
    const ext = mediaFile.name.split('.').pop() || (mediaKind === 'image' ? 'jpg' : 'mp4');
    const safeEmail = (memberEmail || 'member').replace(/[^a-z0-9]/gi, '_');
    const path = `board/${safeEmail}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error: uploadErr } = await supabase.storage
      .from('family-media')
      .upload(path, mediaFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: mediaFile.type,
      });
    if (uploadErr) {
      toast.error('Upload failed. Try a smaller file.');
      return null;
    }
    const { data } = supabase.storage.from('family-media').getPublicUrl(path);
    return { url: data.publicUrl, type: mediaKind === 'image' ? 'image' : 'video' };
  };

  const handlePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isMember) {
      openMembershipModal();
      return;
    }
    if (!content.trim() && !mediaFile) {
      toast.error('Write something or attach a photo/video');
      return;
    }
    if (!authorName.trim()) {
      toast.error('Add your name so family knows who posted');
      return;
    }
    setPosting(true);
    localStorage.setItem('kinscircle_board_name', authorName.trim());

    let media: { url: string; type: string } | null = null;
    if (mediaFile) {
      media = await uploadMedia();
      if (!media) {
        setPosting(false);
        return;
      }
    }

    const { error } = await supabase.from('board_posts').insert({
      author_email: memberEmail || 'member@kinscircle',
      author_name: authorName.trim(),
      content: content.trim(),
      media_url: media?.url || null,
      media_type: media?.type || null,
    });
    setPosting(false);
    if (error) {
      toast.error('Could not post. Try again.');
      return;
    }
    setContent('');
    clearMedia();
    toast.success('Posted to the family board');
    fetchPosts();
  };

  const handleDelete = async (post: BoardPost) => {
    if (!isMember) return;
    if (post.author_email !== memberEmail) {
      toast.error('You can only delete your own posts');
      return;
    }
    const { error } = await supabase.from('board_posts').delete().eq('id', post.id);
    if (error) {
      toast.error('Could not delete');
      return;
    }
    toast.success('Post removed');
    fetchPosts();
  };

  return (
    <section id="board" className="py-20 bg-gradient-to-b from-[#FAF6F0] to-[#F5EDE0]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100/80 rounded-full mb-4 border border-amber-200">
            <Users className="w-4 h-4 text-amber-800" />
            <span className="text-sm font-medium text-amber-900">Family Message Board</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-amber-950 mb-3">The Family Bulletin</h2>
          <p className="text-amber-800/80 max-w-2xl mx-auto">
            Post updates, photos, or short videos. Everyone in your KinsCircle can see what's shared here.
          </p>
        </div>

        {/* Composer */}
        <div className="bg-white rounded-2xl shadow-lg border border-amber-200/60 p-6 mb-8">
          {!isMember ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center">
                <Lock className="w-6 h-6 text-amber-800" />
              </div>
              <h3 className="font-serif text-xl font-bold text-amber-950 mb-1">Members can post</h3>
              <p className="text-sm text-amber-800/70 mb-4">
                Become a KinsCircle member to post updates, photos, and videos everyone in the family can see.
              </p>
              <Button
                onClick={openMembershipModal}
                className="bg-gradient-to-r from-amber-700 to-amber-900 hover:from-amber-800 hover:to-amber-950 text-amber-50 rounded-full"
              >
                Become a Member
              </Button>
            </div>
          ) : (
            <form onSubmit={handlePost} className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-700 to-green-800 text-amber-50 flex items-center justify-center font-serif font-bold shrink-0">
                  {(authorName || memberEmail || '?').charAt(0).toUpperCase()}
                </div>
                <Input
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  placeholder="Your name (e.g., Aunt Rose)"
                  className="bg-amber-50/50 border-amber-200"
                />
              </div>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What's happening in the family? Share news, an announcement, or a memory..."
                rows={3}
                className="bg-amber-50/50 border-amber-200 resize-none"
              />

              {/* Media preview */}
              {mediaPreview && (
                <div className="relative inline-block rounded-xl overflow-hidden border border-amber-200 bg-amber-50">
                  {mediaKind === 'image' ? (
                    <img src={mediaPreview} alt="preview" className="max-h-64 object-contain" />
                  ) : (
                    <video src={mediaPreview} controls className="max-h-64" />
                  )}
                  <button
                    type="button"
                    onClick={clearMedia}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full"
                    aria-label="Remove media"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
              />

              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-amber-300 text-amber-900 hover:bg-amber-50 gap-2"
                  >
                    <ImagePlus className="w-4 h-4" />
                    Photo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-amber-300 text-amber-900 hover:bg-amber-50 gap-2"
                  >
                    <Video className="w-4 h-4" />
                    Short Video
                  </Button>
                  <span className="text-xs text-amber-800/60 hidden sm:inline">
                    Up to {MAX_IMAGE_MB}MB photo · {MAX_VIDEO_SECONDS}s / {MAX_VIDEO_MB}MB video
                  </span>
                </div>
                <Button
                  type="submit"
                  disabled={posting || (!content.trim() && !mediaFile)}
                  className="bg-gradient-to-r from-amber-700 to-amber-900 hover:from-amber-800 hover:to-amber-950 text-amber-50 rounded-full gap-2"
                >
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {posting ? 'Posting...' : 'Post to Board'}
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Posts list */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-xl font-bold text-amber-950 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-amber-800" />
            Recent Posts
            <span className="text-sm font-sans font-normal text-amber-800/60">({posts.length})</span>
          </h3>
          <button
            onClick={fetchPosts}
            className="text-sm text-amber-800 hover:text-amber-950 flex items-center gap-1"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="space-y-3">
          {posts.length === 0 && !loading && (
            <div className="text-center py-12 bg-white/60 rounded-2xl border border-dashed border-amber-300">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 text-amber-400" />
              <p className="text-amber-800/70">No posts yet. Be the first to share something with the family.</p>
            </div>
          )}
          {posts.map((post) => {
            const isOwn = post.author_email === memberEmail;
            return (
              <article
                key={post.id}
                className="bg-white rounded-xl shadow-sm border border-amber-200/60 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-700 to-green-800 text-amber-50 flex items-center justify-center font-serif font-bold shrink-0">
                    {post.author_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-amber-950">{post.author_name}</span>
                        {isOwn && (
                          <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                            You
                          </span>
                        )}
                        <span className="text-xs text-amber-800/60">· {formatTime(post.created_at)}</span>
                      </div>
                      {isOwn && (
                        <button
                          onClick={() => handleDelete(post)}
                          className="p-1.5 text-amber-800/60 hover:text-rose-600 hover:bg-rose-50 rounded-full transition-colors"
                          aria-label="Delete post"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {post.content && (
                      <p className="text-amber-900 whitespace-pre-wrap break-words mb-3">{post.content}</p>
                    )}
                    {post.media_url && post.media_type === 'image' && (
                      <a
                        href={post.media_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block rounded-xl overflow-hidden border border-amber-200 bg-amber-50 max-w-lg"
                      >
                        <img
                          src={post.media_url}
                          alt="Shared by family"
                          className="w-full max-h-96 object-cover hover:opacity-95 transition-opacity"
                          loading="lazy"
                        />
                      </a>
                    )}
                    {post.media_url && post.media_type === 'video' && (
                      <div className="rounded-xl overflow-hidden border border-amber-200 bg-black max-w-lg">
                        <video
                          src={post.media_url}
                          controls
                          preload="metadata"
                          className="w-full max-h-96"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MessageBoard;
